<script setup>
import { router } from '@inertiajs/vue3'
import { useToast } from 'primevue/usetoast'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import AdminTabsLayout from '@/Layouts/AdminTabsLayout.vue'
import AppLayout from '@/Layouts/AppLayout.vue'

defineOptions({ layout: [AppLayout, AdminTabsLayout] })

const props = defineProps({
  template_options: Object,
})

const { t } = useI18n()
const toast = useToast()
const isLoading = ref(false)
const tagValues = ref({})

const addTag = async (templateOption, value) => {
  if (!value) return

  isLoading.value = true

  router.post(
    route('admin.template-option.store'),
    {
      template_option_id: templateOption.id,
      value,
    },
    {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => {
        toast.add({
          severity: 'success',
          summary: t('messages.added'),
          detail: t('messages.admin.templateOption.addedSuccessDetail'),
          life: 3000,
        })
        tagValues.value[templateOption.id] = null
      },
      onError: () => {
        toast.add({
          severity: 'error',
          summary: t('messages.error'),
          detail: t('messages.admin.templateOption.addedErrorDetail'),
          life: 3000,
        })
      },
      onFinish: () => {
        isLoading.value = false
      },
    },
  )
}

const remove = (templateOptionId, tag) => {
  isLoading.value = true

  router.delete(route('admin.template-option.destroy', tag.id), {
    preserveScroll: true,
    preserveState: true,
    onSuccess: () => {
      toast.add({
        severity: 'success',
        summary: t('messages.deleted'),
        detail: t('messages.admin.templateOption.deletedSuccessDetail'),
        life: 3000,
      })
    },
    onError: () => {
      toast.add({
        severity: 'error',
        summary: t('messages.error'),
        detail: t('messages.admin.templateOption.deletedErrorDetail'),
        life: 3000,
      })
    },
    onFinish: () => {
      isLoading.value = false
    },
  })
}
</script>

<template>
  <div class="mt-3">
    <Tabs value="ehrCommunal" content-class="mt-1">
      <TabList>
        <Tab v-for="(_, key) in template_options" :key="key" :value="key">
          {{ $t(`messages.admin.templateOption.category.${key}`) }}
        </Tab>
      </TabList>
      <TabPanels>
        <TabPanel
          class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
          v-for="(category, key) in template_options"
          :value="key"
        >
          <Card v-for="templateOption in category" :key="templateOption.id">
            <template #title>
              {{ $t(`messages.detail.${templateOption.category}.${templateOption.name}`) }}
            </template>
            <template #content>
              <div class="border-0 p-0">
                <form @submit.prevent="addTag(templateOption, tagValues[templateOption.id])">
                  <InputGroup class="input-group-sm mb-2">
                    <InputText
                      v-model="tagValues[templateOption.id]"
                      :placeholder="$t(`messages.admin.templateOption.addInstruction`)"
                      class="form-control"
                    ></InputText>
                    <InputGroupAddon>
                      <Button
                        @click="addTag(templateOption, tagValues[templateOption.id])"
                        variant="primary"
                        class="btn-sm"
                      >
                        {{ $t(`messages.admin.templateOption.add`) }}
                      </Button>
                    </InputGroupAddon>
                  </InputGroup>
                </form>
                <div class="flex flex-wrap gap-1">
                  <Chip
                    v-for="tag in templateOption.template_option_value"
                    :key="tag.id"
                    :label="tag.value"
                    removable
                    @remove="remove(templateOption.id, tag)"
                  >
                    <template #removeicon="{ removeCallback, keydownCallback }">
                      <span class="pi pi-times-circle hover:cursor-pointer hover:text-red-500" @click="removeCallback" @keydown="keydownCallback"></span>
                    </template>
                  </Chip>
                </div>
              </div>
            </template>
          </Card>
        </TabPanel>
      </TabPanels>
    </Tabs>
  </div>
</template>
